import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageModal from '../components/PageModal';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';
import { Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const navigate = useNavigate();
  const { getCartCount } = useCart();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false
  });
  const [errors, setErrors] = useState({});
  const [modalPage, setModalPage] = useState(null); // {slug, title} or null

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'Ad gerekli';
    if (!formData.last_name.trim()) newErrors.last_name = 'Soyad gerekli';
    if (!formData.email.includes('@')) newErrors.email = 'Geçerli bir email girin';
    if (formData.password.length < 8) newErrors.password = 'Şifre en az 8 karakter olmalı';
    else if (!/\d/.test(formData.password)) newErrors.password = 'Şifre en az bir rakam içermelidir';
    else if (!/[A-Za-zğüşıöçĞÜŞİÖÇ]/.test(formData.password)) newErrors.password = 'Şifre en az bir harf içermelidir';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    if (!formData.agreeTerms) newErrors.agreeTerms = 'Kullanım koşullarını kabul etmelisiniz';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || null,
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Auto-login after registration
        if (data.access_token) {
          // Primary auth: real JWT in localStorage (cross-browser safe).
          localStorage.setItem('userToken', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          window.dispatchEvent(new Event('loginStateChanged'));
          alert('✅ Kayıt başarılı!');
          navigate('/profile');
        } else {
          alert('✅ Kayıt başarılı! Şimdi giriş yapabilirsiniz.');
          navigate('/login');
        }
      } else {
        setErrors({ submit: data.detail || 'Kayıt başarısız' });
      }
    } catch (error) {
      console.error('Register error:', error);
      setErrors({ submit: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Üye Ol - Cep Premium"
        description="Cep Premium'a üye olun, avantajlı fiyatlardan yararlanın"
      />
      <Header cartCount={getCartCount()} />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-center text-gray-800 mb-2">Üye Ol</h1>
          <p className="text-center text-gray-600 mb-8">
            Hızlı alışveriş için üye olun
          </p>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {errors.submit}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ad *
              </label>
              <input
                type="text"
                required
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent ${errors.first_name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Adınız"
              />
              {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Soyad *
              </label>
              <input
                type="text"
                required
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent ${errors.last_name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Soyadınız"
              />
              {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Adresi *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="ornek@email.com"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefon (Opsiyonel)
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                placeholder="0555 123 4567"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Şifre *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent pr-12 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="En az 6 karakter"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Şifre Tekrar *
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Şifrenizi tekrar girin"
              />
              {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
            </div>

            {/* Terms */}
            <div>
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={formData.agreeTerms}
                  onChange={(e) => setFormData({...formData, agreeTerms: e.target.checked})}
                  className="mt-1 rounded border-gray-300 text-red-600 focus:ring-red-600"
                />
                <span className="ml-2 text-sm text-gray-600">
                  <button
                    type="button"
                    onClick={() => setModalPage({ slug: 'kullanim-kosullari', title: 'Kullanım Koşulları' })}
                    data-testid="open-terms-modal"
                    className="text-red-600 hover:underline bg-transparent border-0 p-0 cursor-pointer font-inherit"
                  >
                    Kullanım Koşullarını
                  </button> ve{' '}
                  <button
                    type="button"
                    onClick={() => setModalPage({ slug: 'privacy', title: 'Gizlilik Politikası' })}
                    data-testid="open-privacy-modal"
                    className="text-red-600 hover:underline bg-transparent border-0 p-0 cursor-pointer font-inherit"
                  >
                    Gizlilik Politikasını
                  </button> kabul ediyorum
                </span>
              </label>
              {errors.agreeTerms && <p className="text-red-500 text-sm mt-1">{errors.agreeTerms}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed mt-6"
            >
              {loading ? 'Kayıt Oluşturuluyor...' : 'Üye Ol'}
            </button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Zaten üye misiniz?{' '}
              <Link to="/login" className="text-red-600 font-semibold hover:text-red-700">
                Giriş Yap
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
      
      {/* Terms/Privacy Modal */}
      {modalPage && (
        <PageModal
          slug={modalPage.slug}
          title={modalPage.title}
          onClose={() => setModalPage(null)}
        />
      )}
    </div>
  );
};

export default RegisterPage;
